import { useState } from 'react';
import { CalendarOff, Send } from 'lucide-react';
import SheetModal from '../../components/SheetModal';
import Dropdown from '../../components/receptura/Dropdown';
import { SheetInput, SheetTextarea, SheetButton, SheetRow, SheetAlert } from '../MojeSmena/SheetUI';
import { SHEET_TOKENS, getTextColors } from '../MojeSmena/sheetStyle';

const AKCENT = '#0CC0DF';

const TYPY = [
    { value: 'dovolena', label: 'Dovolená' },
    { value: 'nemoc',    label: 'Nemoc' },
    { value: 'osobni',   label: 'Osobní' },
    { value: 'jine',     label: 'Jiné' },
];

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const pocetDnu = (od, doo) => {
    if (!od || !doo) return 0;
    const a = new Date(`${od}T00:00:00`);
    const b = new Date(`${doo}T00:00:00`);
    if (b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
};

const sklonujDny = (n) => (n === 1 ? 'den' : (n >= 2 && n <= 4 ? 'dny' : 'dní'));

/**
 * Žádost o nepřítomnost z Mého měsíce.
 *
 * Vstupuje se sem z konkrétního dne (klepnutím v kalendáři), takže `od` i `do`
 * jsou předvyplněné na ten den — „potřebuju volno zrovna tenhle den" je
 * nejčastější případ a nestojí ani dotyk navíc. Rozsah se roztáhne ručně.
 *
 * Posílá na `POST /api/me/nedostupnosti`, které založí záznam ve stavu `ceka`
 * a pošle notifikaci schvalovatelům.
 *
 * Typ jde přes naši `Dropdown` komponentu, ne přes `SheetSelect` — ten je
 * postavený na nativním `<select>`, který se v téhle aplikaci nepoužívá.
 */
export default function ZadostOVolnoSheet({ datum = null, onClose, onSuccess, isDark }) {
    const dnes = new Date().toISOString().slice(0, 10);
    const vychozi = datum || dnes;

    const [od, setOd] = useState(vychozi);
    const [doData, setDoData] = useState(vychozi);
    const [typ, setTyp] = useState('osobni');
    const [hodiny, setHodiny] = useState('');
    const [poznamka, setPoznamka] = useState('');
    const [odesila, setOdesila] = useState(false);
    const [chyba, setChyba] = useState(null);

    const barvy = getTextColors(isDark);
    const dnu = pocetDnu(od, doData);
    const hod = Number(String(hodiny).replace(',', '.'));
    const hodinyOk = hod >= 0.5 && hod <= 24;

    const odeslat = async () => {
        if (dnu < 1) {
            setChyba('Datum „do“ nemůže být dřív než „od“.');
            return;
        }
        if (!hodinyOk) {
            setChyba('Zadej počet hodin za den (0,5–24).');
            return;
        }
        setOdesila(true);
        setChyba(null);
        try {
            const res = await fetch('/api/me/nedostupnosti', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrf(),
                },
                body: JSON.stringify({ od, do: doData, hodiny: hod, typ, poznamka: poznamka.trim() || null }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || data?.message || `Chyba ${res.status}`);
            }
            onSuccess?.();
            onClose?.();
        } catch (e) {
            setChyba(e.message || 'Žádost se nepodařilo odeslat.');
        } finally {
            setOdesila(false);
        }
    };

    const stylLabelu = {
        fontSize: SHEET_TOKENS.typography.label.size,
        fontWeight: SHEET_TOKENS.typography.label.weight,
        letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
        textTransform: SHEET_TOKENS.typography.label.textTransform,
        color: barvy.labelMuted,
    };

    return (
        <SheetModal
            onClose={onClose}
            isDark={isDark}
            ikona={CalendarOff}
            barva={AKCENT}
            nadpis="Požádat o volno"
            podnadpis={dnu > 0 ? `${dnu} ${sklonujDny(dnu)}` : 'Vyber období'}
            zavritLze={!odesila}
            paticka={(
                <SheetButton variant="primary" accent={AKCENT} accent2="#0AA6C2"
                    icon={<Send size={16} />} busy={odesila} busyLabel="Odesílám…"
                    disabled={dnu < 1 || !hodinyOk} isDark={isDark} onClick={odeslat}>
                    Odeslat žádost
                </SheetButton>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: SHEET_TOKENS.spacing.rowGap }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: SHEET_TOKENS.spacing.controlGap, minWidth: 0 }}>
                    <span style={stylLabelu}>Důvod</span>
                    <Dropdown value={typ} onChange={setTyp} options={TYPY} isDark={isDark}
                        height={SHEET_TOKENS.sizes.inputHeight} accent={AKCENT} />
                </div>

                {/* `SheetInput` propouští `style` až na samotný <input>, takže
                    poloviny řádku musí držet obal, ne komponenta. */}
                <SheetRow>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <SheetInput label="Od" type="date" value={od} max={doData} isDark={isDark} accent={AKCENT}
                            onChange={e => { setOd(e.target.value); setChyba(null); }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <SheetInput label="Do" type="date" value={doData} min={od} isDark={isDark} accent={AKCENT}
                            onChange={e => { setDoData(e.target.value); setChyba(null); }} />
                    </div>
                </SheetRow>

                <SheetInput label="Hodin za den" type="number" inputMode="decimal" min="0.5" max="24" step="0.5"
                    value={hodiny} placeholder="např. 8" isDark={isDark} accent={AKCENT}
                    hint={dnu > 1 && hodinyOk ? `Celkem ${(hod * dnu).toLocaleString('cs-CZ')} h` : undefined}
                    onChange={e => { setHodiny(e.target.value); setChyba(null); }} />

                <SheetTextarea label="Poznámka" hint="Nepovinná" rows={3} maxLength={500}
                    value={poznamka} onChange={e => setPoznamka(e.target.value)}
                    placeholder="Např. svatba v rodině" isDark={isDark} accent={AKCENT} />

                {chyba && <SheetAlert kind="error" isDark={isDark}>{chyba}</SheetAlert>}

                <p style={{
                    fontSize: SHEET_TOKENS.typography.hint.size, fontWeight: SHEET_TOKENS.typography.hint.weight,
                    color: barvy.textMuted, lineHeight: 1.5, margin: 0,
                }}>
                    Žádost půjde manažerovi ke schválení. Dokud ji neschválí, zůstane v kalendáři
                    označená jako čekající a směny se kvůli ní neruší.
                </p>
            </div>
        </SheetModal>
    );
}
